import { Link, NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const NAV = [["Product", "/product"], ["Solutions", "/solutions/small-business"], ["Pricing", "/pricing"], ["Docs", "/docs/getting-started"], ["About", "/about"]];

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(!localStorage.getItem("cr_cookie_consent"));
  }, []);
  if (!show) return null;
  const choose = (v) => {
    localStorage.setItem("cr_cookie_consent", v);
    setShow(false);
  };
  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-gray-900 text-gray-200 border-t border-gray-700 p-4" data-testid="cookie-banner">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-3 md:items-center justify-between text-sm">
        <p>We use strictly necessary cookies for sign-in and security. No advertising or tracking cookies are set. See our <Link to="/legal/privacy" className="underline text-white">Privacy Policy</Link>.</p>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => choose("necessary")} className="px-4 py-2 border border-gray-600 hover:border-white" data-testid="cookie-necessary-btn">Necessary only</button>
          <button onClick={() => choose("accepted")} className="px-4 py-2 bg-red-600 text-white hover:bg-red-700" data-testid="cookie-accept-btn">Accept</button>
        </div>
      </div>
    </div>
  );
}

export default function SiteLayout() {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-link"><Logo dark /></Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map(([l, p]) => (
              <NavLink key={p} to={p} data-testid={`nav-${l.toLowerCase()}-link`} className={({ isActive }) => `text-sm ${isActive ? "text-white" : "text-gray-400 hover:text-white"}`}>{l}</NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {session ? (
              <Link to="/portal" className="px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700" data-testid="nav-portal-btn">Open portal</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-gray-300 hover:text-white" data-testid="nav-login-link">Sign in</Link>
                <Link to="/register" className="px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700" data-testid="nav-trial-btn">Start 15-Day Trial</Link>
              </>
            )}
          </div>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="md:hidden px-6 pb-4 flex flex-col gap-3" data-testid="nav-mobile-menu">
            {NAV.map(([l, p]) => <Link key={p} to={p} onClick={() => setOpen(false)} className="text-gray-300">{l}</Link>)}
            <Link to={session ? "/portal" : "/register"} onClick={() => setOpen(false)} className="px-4 py-2 bg-red-600 text-white text-center">{session ? "Open portal" : "Start 15-Day Trial"}</Link>
            {!session && <Link to="/login" onClick={() => setOpen(false)} className="text-gray-300">Sign in</Link>}
          </div>
        )}
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="bg-gray-900 text-gray-400 border-t border-gray-800" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
          <div className="col-span-2"><Logo dark /><p className="mt-4 max-w-xs">Your AI IT Administrator. Monitoring, diagnosing and resolving IT problems 24/7.</p></div>
          <div className="flex flex-col gap-2"><span className="text-white font-semibold">Product</span><Link to="/product">Overview</Link><Link to="/pricing">Pricing</Link><Link to="/status">System status</Link></div>
          <div className="flex flex-col gap-2"><span className="text-white font-semibold">Resources</span><Link to="/docs/getting-started">Documentation</Link><Link to="/docs/api">API</Link><Link to="/contact">Contact</Link><a href="mailto:admin@dsouzainfotech.com" data-testid="footer-email-admin">admin@dsouzainfotech.com</a><a href="mailto:support@centraldatastorage.com" data-testid="footer-email-support">support@centraldatastorage.com</a></div>
          <div className="flex flex-col gap-2"><span className="text-white font-semibold">Legal</span><Link to="/legal/privacy">Privacy</Link><Link to="/legal/terms">Terms</Link><Link to="/legal/acceptable-use">Acceptable Use</Link><Link to="/legal/responsible-automation">Responsible Automation</Link></div>
        </div>
        <div className="border-t border-gray-800 py-5 text-center text-xs">© 2026 CodeRed AI IT Autopilot. All rights reserved.</div>
      </footer>
      <CookieBanner />
    </div>
  );
}
